import { useMemo, useEffect, useCallback } from "react"
import { parse as parseQueryParams } from "query-string"

import { useLocation } from "store/contexts/location"
import contentfulPromotionalPlacementsHelper from "utils/contentfulPromotionalPlacements"

const usePromotions = (
  article,
  contentfulAd,
  contentfulPromotionalPlacements,
  inlineAdIndex,
  isTop,
  loadedArticlesPromotion,
  handleLoadedArticlesPromotion,
  hasAdsOnly,
) => {
  const { elements } = article

  const { location } = useLocation()
  const queryParams = parseQueryParams(location.search)
  const fromEmail = queryParams?.utm_medium === "email"

  const promotionalPlacements = useMemo(
    () =>
      contentfulPromotionalPlacementsHelper.getLayoutPromotionalPlacements(
        article,
        contentfulPromotionalPlacements,
      ),
    [article, contentfulPromotionalPlacements],
  )

  const getAd = useCallback(() => {
    const ad =
      contentfulAd.mobile || contentfulAd.desktop
        ? {
            type: "contentful-ad",
            ...contentfulAd,
          }
        : { type: "ad", index: inlineAdIndex.current }

    if (!(contentfulAd.mobile || contentfulAd.desktop)) {
      inlineAdIndex.current += 1
    }

    return ad
  }, [contentfulAd, inlineAdIndex])

  const usedPlacementIndexes = {
    "In Content": {},
    "Below Content": {},
  }

  const getPromotionalPlacement = useCallback(
    (layout, index) => {
      const placements =
        layout === "In Content"
          ? promotionalPlacements[layout][index === 0 ? "highVisibility" : "mediumVisibility"]
          : promotionalPlacements[layout].lowVisibility

      if (placements.length) {
        let idx = 0
        while (idx < placements.length) {
          const placement = placements[idx]
          const { internalId } = placement
          if (!(internalId in usedPlacementIndexes[layout])) {
            if (
              (layout === "In Content" &&
                ((index === 0 && placement.highVisibilityRPM === 0) ||
                  (index > 0 && placement.mediumVisibilityRPM === 0))) ||
              (layout === "Below Content" && placement.lowVisibilityRPM === 0)
            ) {
              idx += 1
              continue
            }

            usedPlacementIndexes[layout][internalId] = true

            return {
              type: "promotional-placement",
              ...placement,
              below: layout === "Below Content",
            }
          }

          idx += 1
        }
      }

      return null
    },
    [promotionalPlacements, usedPlacementIndexes, fromEmail],
  )

  useEffect(() => {
    if (elements) {
      const totalPromotions = elements.filter(({ type }) => type === "promotion").length

      if (totalPromotions > 0) {
        const middlePromotionIndex = Math.floor(totalPromotions / 2)
        let promotionIndex = 0
        let adCount = 0
        const maxAds = 10

        elements.forEach((element, index) => {
          const { uuid, type } = element

          if (type === "promotion") {
            let item

            if (hasAdsOnly) {
              item = getAd()
            } else if (promotionIndex === totalPromotions - 1) {
              // to insert a "Below Content" promotional placement, the promotion element
              // has to be the last element in the article
              if (index === elements.length - 1) {
                item = { type: "dummy" }
              } else {
                item = getPromotionalPlacement("In Content", promotionIndex) || getAd()
              }
            } else if (promotionIndex === 0) {
              item = getPromotionalPlacement("In Content", promotionIndex) || getAd()
            } else if (promotionIndex === middlePromotionIndex) {
              item =
                totalPromotions <= 4
                  ? getAd()
                  : getPromotionalPlacement("In Content", promotionIndex) || getAd()
            } else {
              item = getAd()
            }

            if (item.type === "ad") {
              adCount += 1

              if (adCount > maxAds) {
                item.type = "dummy"
              }
            }

            if (item?.contentful_id) {
              handleLoadedArticlesPromotion()
            }

            elements[index] = { uuid, ...item }
            promotionIndex += 1
          }
        })
      }
    }
  }, [elements, getAd, getPromotionalPlacement, handleLoadedArticlesPromotion])
}

export default usePromotions
